import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Button,
  EmptyState,
  EmptyStateTitle,
  PageLoader,
  Panel,
  PanelHeader,
  PanelInset,
  PanelTitle,
  Paragraph,
  Table,
  TableTbody,
  TableTd,
  TableTh,
  TableThead,
  TableTr,
} from '@customerio/pluma-components/react';
import { buildLogRows, buildRelationLookups, useActivityLogEnvironment } from '@/services/db/activity-log/queries';
import { getObjectLogsQueryOptions } from '@/services/db/object/queries';
import { useProductTaxonomy } from '@/services/product-taxonomy';
import { activityLogTypeLabel } from '@/pages/workspaces/activity-logs/activity-log-types';
import { NameCell, TypeCell } from '@/pages/workspaces/activity-logs/components/cells';
import { TimestampCell } from '@/pages/workspaces/activity-logs/components/columns';
import { useObjectDetail } from './context';

const RECENT_ACTIVITY_LIMIT = 5;

export function ObjectRecentActivity() {
  const { record, workspaceId } = useObjectDetail();
  const taxonomy = useProductTaxonomy();
  const { identifiers } = useActivityLogEnvironment(String(workspaceId));
  const query = useQuery(
    getObjectLogsQueryOptions(workspaceId, record.identifiers.cio_object_id, { limit: RECENT_ACTIVITY_LIMIT }),
  );

  const typeTerms = useMemo(
    () => ({
      campaignSingular: taxonomy.term('campaigns.titleSingular'),
      personSingular: taxonomy.term('people.titleSingular'),
      personPlural: taxonomy.term('people.titlePlural'),
    }),
    [taxonomy],
  );
  const rows = useMemo(() => {
    if (query.data == null || !('logs' in query.data)) return [];
    const pages = [query.data];
    return buildLogRows(pages, buildRelationLookups(pages));
  }, [query.data]);

  return (
    <Panel>
      <PanelHeader pb="0" aria-labelledby="recent-activity-header">
        <PanelTitle id="recent-activity-header" size="md">
          Recent Activity
        </PanelTitle>
      </PanelHeader>
      <PanelInset pb="0">
        <Table caption="Recent Activity Table" layout="fixed" mt="200">
          <TableThead>
            <TableTr>
              <TableTh textAlign="left">Activity type</TableTh>
              <TableTh textAlign="left">Activity name</TableTh>
              <TableTh textAlign="right">Timestamp</TableTh>
            </TableTr>
          </TableThead>
          <TableTbody>
            {query.isPending ? (
              <TableTr>
                <TableTd px="300" colSpan={3}>
                  <PageLoader />
                </TableTd>
              </TableTr>
            ) : query.isError ? (
              <TableTr>
                <TableTd colSpan={3} px="300">
                  <Paragraph variant="secondary">Could not load recent attribute changes.</Paragraph>
                  <Paragraph textAlign="center">
                    <Button
                      isLoading={query.isFetching}
                      variant="secondary"
                      size="sm"
                      onClick={() => void query.refetch()}
                      icon="reset"
                      iconPosition="leading"
                    >
                      Try again
                    </Button>
                  </Paragraph>
                </TableTd>
              </TableTr>
            ) : rows.length === 0 ? (
              <TableTr>
                <TableTd colSpan={3}>
                  <EmptyState>
                    <EmptyStateTitle icon="activity">No recent activity</EmptyStateTitle>
                  </EmptyState>
                </TableTd>
              </TableTr>
            ) : (
              rows.map((row) => (
                <TableTr key={row.log.id}>
                  <TableTd>
                    <TypeCell
                      row={row}
                      label={activityLogTypeLabel(row.log, typeTerms)}
                      workspaceId={String(workspaceId)}
                      personSingular={typeTerms.personSingular}
                    />
                  </TableTd>
                  <TableTd>
                    <NameCell row={row} workspaceId={String(workspaceId)} identifiers={identifiers} />
                  </TableTd>
                  <TableTd textAlign="right">
                    <TimestampCell value={row.log.timestamp} />
                  </TableTd>
                </TableTr>
              ))
            )}
          </TableTbody>
        </Table>
      </PanelInset>
    </Panel>
  );
}
